import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { Save, User, Mail, Phone, Key } from 'lucide-react';
import useAuth from '../hooks/useAuth';
import { getUserById, updateUser, changePassword } from '../api/users';
import useForm from '../hooks/useForm';

const ProfilePage = () => {
    const { user, logout } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPasswordForm, setShowPasswordForm] = useState(false);

    const initialValues = {
        fullName: '',
        email: '',
        phoneNumber: ''
    };

    const passwordInitialValues = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    };

    const { values, setValues, handleChange, errors, validateForm } = useForm(initialValues);
    const {
        values: passwordValues,
        handleChange: handlePasswordChange,
        errors: passwordErrors,
        validateForm: validatePasswordForm
    } = useForm(passwordInitialValues);

    useEffect(() => {
        const fetchUserData = async () => {
            if (!user) return;

            try {
                setLoading(true);
                const userData = await getUserById(user.id);

                setValues({
                    fullName: userData.fullName || '',
                    email: userData.email || '',
                    phoneNumber: userData.phoneNumber || ''
                });
            } catch (err) {
                setError('Failed to load user data. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [user, setValues]);

    const validationSchema = {
        fullName: {
            required: true,
            errorMessage: 'Full name is required'
        },
        email: {
            required: true,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            errorMessage: 'Valid email is required'
        }
    };

    const passwordValidationSchema = {
        currentPassword: {
            required: true,
            errorMessage: 'Current password is required'
        },
        newPassword: {
            required: true,
            min: 8,
            errorMessage: 'New password must be at least 8 characters'
        },
        confirmPassword: {
            required: true,
            errorMessage: 'Please confirm your new password'
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validate form
        const isValid = validateForm(validationSchema);
        if (!isValid) {
            return;
        }

        try {
            setLoading(true);
            await updateUser(user.id, values);
            setSuccess('Profile updated successfully');
        } catch (err) {
            setError(err.message || 'An error occurred while updating your profile');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validate form
        const isValid = validatePasswordForm(passwordValidationSchema);
        if (!isValid) {
            return;
        }

        // Check if passwords match
        if (passwordValues.newPassword !== passwordValues.confirmPassword) {
            setError("New passwords don't match");
            return;
        }

        try {
            setLoading(true);
            await changePassword(user.id, passwordValues.currentPassword, passwordValues.newPassword);
            setSuccess('Mot de passe modifié avec succes.');

            // Clear password fields
            passwordValues.currentPassword = '';
            passwordValues.newPassword = '';
            passwordValues.confirmPassword = '';

            // Log out the user after 2 seconds
            setTimeout(() => {
                logout();
            }, 2000);
        } catch (err) {
            setError(err.message || 'An error occurred while changing your password');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordForm = () => {
        setShowPasswordForm(!showPasswordForm);
        setError('');
        setSuccess('');
    };

    return (
        <Container>
            <h1 className="mb-4">Mon Profil</h1>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Row>
                <Col md={6} className="mb-4">
                    <Card className="shadow-sm">
                        <Card.Header>
                            <h5 className="mb-0">Informations Personnelles</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom Complet</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <User size={18} />
                                        </span>
                                        <Form.Control
                                            type="text"
                                            name="fullName"
                                            value={values.fullName}
                                            onChange={handleChange}
                                            placeholder="Entrez votre nom complet"
                                            required
                                        />
                                    </div>
                                    {errors.fullName && <Form.Text className="text-danger">{errors.fullName}</Form.Text>}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <Mail size={18} />
                                        </span>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={values.email}
                                            onChange={handleChange}
                                            placeholder="Entrez votre email"
                                            required
                                        />
                                    </div>
                                    {errors.email && <Form.Text className="text-danger">{errors.email}</Form.Text>}
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>Numéro de Téléphone</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <Phone size={18} />
                                        </span>
                                        <Form.Control
                                            type="text"
                                            name="phoneNumber"
                                            value={values.phoneNumber}
                                            onChange={handleChange}
                                            placeholder="Entrez votre numéro de téléphone"
                                        />
                                    </div>
                                </Form.Group>

                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={loading}
                                    className="w-100"
                                >
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : (
                                        <>
                                            <Save size={16} className="me-1" />
                                            Enregistrer les Modifications
                                        </>
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card className="shadow-sm">
                        <Card.Header>
                            <h5 className="mb-0">Sécurité</h5>
                        </Card.Header>
                        <Card.Body>
                            {!showPasswordForm ? (
                                <div className="text-center py-3">
                                    <Key size={48} className="text-muted mb-3" />
                                    <p className="mb-4">Vous souhaitez modifier votre mot de passe ?</p>
                                    <Button
                                        variant="outline-primary"
                                        onClick={togglePasswordForm}
                                        className="w-100"
                                    >
                                        Changer mon mot de passe
                                    </Button>
                                </div>
                            ) : (
                                <Form onSubmit={handlePasswordSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Mot de passe actuel</Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="currentPassword"
                                            value={passwordValues.currentPassword}
                                            onChange={handlePasswordChange}
                                            placeholder="Entrez votre mot de passe actuel"
                                            required
                                        />
                                        {passwordErrors.currentPassword && (
                                            <Form.Text className="text-danger">{passwordErrors.currentPassword}</Form.Text>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouveau mot de passe</Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="newPassword"
                                            value={passwordValues.newPassword}
                                            onChange={handlePasswordChange}
                                            placeholder="Entrez votre nouveau mot de passe"
                                            required
                                        />
                                        {passwordErrors.newPassword && (
                                            <Form.Text className="text-danger">{passwordErrors.newPassword}</Form.Text>
                                        )}
                                        <Form.Text className="text-muted">
                                            8 caractères minimum, avec chiffres et caractères spéciaux
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label>Confirmer le nouveau mot de passe</Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="confirmPassword"
                                            value={passwordValues.confirmPassword}
                                            onChange={handlePasswordChange}
                                            placeholder="Confirmez votre nouveau mot de passe"
                                            required
                                        />
                                        {passwordErrors.confirmPassword && (
                                            <Form.Text className="text-danger">{passwordErrors.confirmPassword}</Form.Text>
                                        )}
                                    </Form.Group>

                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={togglePasswordForm}
                                            className="flex-grow-1"
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            disabled={loading}
                                            className="flex-grow-1"
                                        >
                                            {loading ? (
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            ) : (
                                                "Changer le mot de passe"
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ProfilePage;